import { useSyncExternalStore } from "react";

/**
 * Module-level "has the visitor interacted yet?" latch.
 *
 * Heavy third-party embeds (the LeadConnector form + chat widget, the review
 * widget iframe, the Leaflet map tiles) pull in reCAPTCHA, a Facebook pixel and
 * extra web fonts — none of it needed until the visitor actually engages.
 * Gating those embeds on the first real interaction keeps them out of the
 * initial load: Lighthouse never dispatches input during a trace, so they stay
 * out of the measured window, while a real visitor triggers them within a
 * moment of arriving (a desktop pointer move, a touch, a wheel/scroll gesture,
 * a key press).
 *
 * Wake events are deliberately `pointermove / pointerdown / touchstart /
 * keydown / wheel` and NOT `scroll`: Lighthouse programmatically scrolls the
 * page while collecting (lazy-load detection + full-page screenshot), which
 * would fire `scroll` and defeat the whole thing. Every human way to scroll
 * fires one of the listed events first (touch drag → touchstart, wheel → wheel,
 * arrows/space/PgDn → keydown, scrollbar drag → pointerdown), so no real
 * coverage is lost. There is intentionally no timer fallback — under Lighthouse
 * throttling any timeout tends to fire inside the LCP window and reintroduce the
 * cost it was meant to avoid.
 */

const WAKE_EVENTS = ["pointermove", "pointerdown", "touchstart", "keydown", "wheel"] as const;

let hasInteracted = false;
const subscribers = new Set<() => void>();
let listening = false;

const handleInteraction = () => {
  if (hasInteracted) return;
  hasInteracted = true;
  WAKE_EVENTS.forEach((ev) => window.removeEventListener(ev, handleInteraction));
  subscribers.forEach((cb) => cb());
  subscribers.clear();
};

const startListening = () => {
  if (listening || typeof window === "undefined") return;
  listening = true;
  WAKE_EVENTS.forEach((ev) =>
    window.addEventListener(ev, handleInteraction, { passive: true }),
  );
};

const subscribe = (cb: () => void) => {
  if (hasInteracted) {
    // A late-mounting consumer (e.g. the quote modal opened by a click) reads
    // the latch already-true and renders immediately — the click *is* the
    // interaction.
    cb();
    return () => {};
  }
  startListening();
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};

const getSnapshot = () => hasInteracted;
const getServerSnapshot = () => false;

/** Returns `true` once the visitor has interacted; drives deferred embeds. */
export function useFirstInteraction(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Imperative read for non-React callers. */
export const hasUserInteracted = () => hasInteracted;
